import IconButton from '@/_application/core/components/ui/icon-button';
import {Image} from '@/_application/core/components/ui/image';
import ProductLoader, {ProductLoaderHorizontal} from '@/_application/core/components/ui/loaders/product-loader';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {Product} from '@/_application/modules/products/model/product';
import {useBestsellerProducts, useHighlightProductBlocks} from '@/framework/products-promotion';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import rangeMap from '@/lib/range-map';
import {useTranslation} from 'next-i18next';
import React, {useCallback, useMemo, useState} from 'react';
import {BlockTypeEnum} from '@/__generated__/__types__';
import {
    HIGHLIGHT_PRODUCT_LIMIT,
    PRODUCTS_LOAD_MORE_COUNT,
} from '@/_application/modules/products/constants/product.constants';

const BlueNextArrowIcon = getImageAssetUrl('icons/blue-next-arrow.png');

const BestSellerProductsMobile = () => {
    const {t} = useTranslation();
    const {products, isLoading} = useBestsellerProducts();
    const {highlightProducts} = useHighlightProductBlocks({blockType: BlockTypeEnum.Bestseller});

    const [limit, setLimit] = useState(4);

    const productsRender = useMemo(() => {
        return highlightProducts?.length ? HIGHLIGHT_PRODUCT_LIMIT : 0;
    }, [highlightProducts?.length]);

    const renderHighlightProducts = useCallback(() => {
        return highlightProducts?.map(
            product =>
                product?.product && (
                    <div key={product.id} className="mt-4">
                        <ProductCard
                            product={product?.product as Product}
                            extraIcon={product.icon ?? ''}
                            extraText={product?.label ?? ''}
                            variant={'horizontal'}
                        />
                    </div>
                ),
        );
    }, [highlightProducts]);

    const renderProductList = useCallback(() => {
        if (!products?.length) {
            return <></>;
        }

        return products
            .filter((_, index) => index < limit - productsRender)
            .map((product, index) => (
                <ProductCard key={`product_${index}`} product={product as Product} className="" />
            ));
    }, [productsRender, products]);

    const handleOnClick = () => {
        setLimit(pre => pre + PRODUCTS_LOAD_MORE_COUNT);
    };

    if (isLoading) {
        return (
            <div data-testid="best-seller-loading" className="px-4 mt-4">
                <div className="mb-4 flex flex-col gap-4">
                    {rangeMap(2, i => (
                        <ProductLoaderHorizontal key={i} uniqueKey={`product-horizontal-${i}`} />
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-4 pb-4 xs:grid-cols-2 sm:grid-cols-3">
                    {rangeMap(4, i => (
                        <ProductLoader key={i} uniqueKey={`product-${i}`} />
                    ))}
                </div>
            </div>
        );
    }

    if (!products?.length && !highlightProducts?.length) {
        return <></>;
    }

    return (
        <div data-testid="best-seller-mobile" className="mx-4 mt-4">
            <div className="mb-4 flex flex-col">{renderHighlightProducts()}</div>

            <div className="grid grid-cols-2 gap-4 pb-4 sm:grid-cols-3">{renderProductList()}</div>

            {limit - productsRender < products?.length && (
                <div className="w-full flex justify-center items-center">
                    <IconButton
                        whileTap={{translateY: 6, backgroundColor: '#21409A', opacity: 0.5}}
                        transition={{ease: 'easeOut'}}
                        onClick={handleOnClick}
                        className="mx-auto flex flex-row-reverse content-center items-center justify-center rounded-2xl px-3 py-2"
                        data-testid="load-more-button"
                        icon={
                            <Image
                                src={BlueNextArrowIcon}
                                alt="Icon"
                                width={8}
                                height={10}
                                className="mb-0.5 ml-2 rotate-90 transform"
                            />
                        }
                        label={t('text-load-more')}
                        labelClassName="text-primary text-xs"
                    />
                </div>
            )}
        </div>
    );
};

export default BestSellerProductsMobile;
